/**
 * Gyro's first-party usage record, the client half: the public face of `usage/featureEvents.ts` (the capture) and
 * `usage/environment.ts` (surface, browser, device), exported from the package for the Office pane and Gyro Admin.
 *
 * The types live here and the implementation imports them (type-only), so this module's declaration stands alone: the
 * vendored build (vendor-pkg/, build-widget-bundle.mjs) ships a flat set of declarations and refuses one that imports a
 * nested module. Spec: the usage analytics contract (2026-10-29), sections 1, 4, 6 and 7.
 */
import * as core from './usage/featureEvents.js';
import * as environment from './usage/environment.js';
// The capture (usage/featureEvents.ts).
export const USAGE_PARAM_KEYS = core.USAGE_PARAM_KEYS;
export const USAGE_SURFACES = core.USAGE_SURFACES;
export const USAGE_QUEUE_KEY = core.USAGE_QUEUE_KEY;
export const USAGE_QUEUE_CAP = core.USAGE_QUEUE_CAP;
export const USAGE_BATCH_SIZE = core.USAGE_BATCH_SIZE;
export const USAGE_SOFT_FLUSH_AT = core.USAGE_SOFT_FLUSH_AT;
export const USAGE_FLUSH_INTERVAL_MS = core.USAGE_FLUSH_INTERVAL_MS;
export const USAGE_SESSION_GAP_MS = core.USAGE_SESSION_GAP_MS;
export const USAGE_BODY_LIMIT_BYTES = core.USAGE_BODY_LIMIT_BYTES;
export const USAGE_BACKOFF_MAX = core.USAGE_BACKOFF_MAX;
export const USAGE_PARAM_STRING_MAX = core.USAGE_PARAM_STRING_MAX;
/** A screen or action name: section 1's shape. */
export const USAGE_NAME_PATTERN = core.USAGE_NAME_PATTERN;
/** The allowlist applied: unknown keys, non-scalar values, empty strings and NaN go; strings are cut at 40 characters. */
export const cleanUsageParams = core.cleanUsageParams;
/** Whether the sink's answer settles a batch: sent (2xx), dropped (400, 413, 422) or kept for a retry (everything else). */
export const usageBatchOutcome = core.usageBatchOutcome;
/** The wait before the next try after `failures` failed flushes in a row: 1 to 5 minutes. */
export const usageBackoffMs = core.usageBackoffMs;
/** The persisted queue, when the stored text is one and belongs to `owner`; anything else (junk, another user's) is nothing. */
export const readPersistedUsageQueue = core.readPersistedUsageQueue;
export const createUsageCapture = core.createUsageCapture;
/** The one capture the widget, the Office pane and Gyro Admin share in a page (telemetry.ts's `track` feeds it). */
export const usageCapture = core.usageCapture;
// The envelope values a page can tell on its own (usage/environment.ts).
/** The event a host answers with its surface (`detail: 'desktop'`), the way shell.html answers 'gyro:window-mode'. */
export const USAGE_SURFACE_EVENT = environment.USAGE_SURFACE_EVENT;
/** The widget's request for it, dispatched once it listens. */
export const USAGE_SURFACE_READY_EVENT = environment.USAGE_SURFACE_READY_EVENT;
export const parseUsageSurface = environment.parseUsageSurface;
/** The widget's surface: the host's answer, else `desktop` for the desktop app's widget, `office` in the pane, `web` elsewhere. */
export const usageSurfaceFor = environment.usageSurfaceFor;
/** `Edge 141`, `Chrome 141`, `Firefox 140`, `Safari 18`, `Opera 120`, or `Browser` when the user agent names none of them. */
export const reducedUserAgent = environment.reducedUserAgent;
/** Phone, tablet or desktop from the user agent (and the touch points, for an iPad that calls itself a Mac). */
export const deviceClassFor = environment.deviceClassFor;
/** The page's own envelope values, from `navigator` (or the one handed in). */
export const clientEnvironment = environment.clientEnvironment;
//# sourceMappingURL=usageRecord.js.map